import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { 
  FaSave, 
  FaEye, 
  FaPaperPlane, 
  FaTimes, 
  FaMobile,
  FaEnvelope,
  FaPlus,
  FaUndo,
  FaCheck,
  FaExclamationTriangle,
  FaCopy,
  FaCode
} from 'react-icons/fa';
import { useNotification } from '../../context/NotificationContext';
import { notificationApi } from '../../api/notifications';
import { 
  validateTemplate as validateTemplateComprehensive,
  generateSampleData,
  renderTemplateWithSampleData,
  suggestVariableCorrections 
} from '../../utils/templateValidation';
import styles from './TemplateEditor.module.css';

/**
 * TemplateEditor Component - Redesigned
 * 
 * Features:
 * - Tabbed interface (In-App / Email)
 * - Inline variable insertion
 * - Live preview modal
 * - Clean, focused layout
 */
const TemplateEditor = ({ 
  clubId, 
  templateType, 
  onSave, 
  onCancel,
  initialTemplate = null 
}) => {
  // Core state
  const [template, setTemplate] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [activeTab, setActiveTab] = useState('inApp'); // 'inApp' | 'email'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Preview & Test state
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(false);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  
  // Variable insertion
  const [activeField, setActiveField] = useState(null);
  const fieldRefs = useRef({});

  const { addNotification } = useNotification();
  
  const notify = useCallback((message, type = 'info') => {
    if (addNotification && typeof addNotification === 'function') {
      addNotification({ message, type });
    } else {
      console.warn('Notification:', message);
    }
  }, [addNotification]);

  // Languages
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' }
  ];

  // Common variables organized by category
  const variableCategories = [
    {
      name: 'User',
      vars: [
        { key: 'user.firstName', label: 'First Name', example: 'John' },
        { key: 'user.lastName', label: 'Last Name', example: 'Doe' },
        { key: 'user.fullName', label: 'Full Name', example: 'John Doe' },
        { key: 'user.email', label: 'Email', example: 'john@email.com' }
      ]
    },
    {
      name: 'Club',
      vars: [
        { key: 'club.name', label: 'Club Name', example: 'Eagles FC' },
        { key: 'club.sport', label: 'Sport', example: 'Soccer' }
      ]
    },
    {
      name: 'Program',
      vars: [
        { key: 'program.name', label: 'Program Name', example: 'Youth Training' },
        { key: 'program.startDate', label: 'Start Date', example: 'Jan 15, 2024' }
      ]
    },
    {
      name: 'Event',
      vars: [
        { key: 'event.title', label: 'Event Title', example: 'Practice Match' },
        { key: 'event.date', label: 'Event Date', example: 'Jan 20, 2024' },
        { key: 'event.location', label: 'Location', example: 'Main Field' }
      ]
    },
    {
      name: 'Other',
      vars: [
        { key: 'message', label: 'Message', example: 'Custom message' },
        { key: 'actionUrl', label: 'Action URL', example: '/dashboard' },
        { key: 'actionLabel', label: 'Action Label', example: 'View Details' }
      ]
    }
  ];

  // Default template structure
  const defaultTemplate = {
    _id: `template_${templateType}`,
    name: templateType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Template',
    type: templateType,
    category: templateType?.split('_')[0] || 'general',
    clubId: clubId,
    isActive: true,
    languages: [{
      code: 'en',
      name: 'English',
      isDefault: true,
      content: {
        subject: 'Notification from {{club.name}}',
        emailHtml: '<h2>Hello {{user.firstName}}!</h2><p>This is a notification from {{club.name}}.</p>',
        emailText: 'Hello {{user.firstName}}!\n\nThis is a notification from {{club.name}}.',
        inAppTitle: 'New Notification',
        inAppMessage: 'You have a new update from {{club.name}}'
      }
    }],
    deliveryConfig: {
      channels: { inApp: true, email: true }
    },
    variables: {
      available: ['user.firstName', 'user.lastName', 'user.fullName', 'user.email', 'club.name', 'program.name', 'event.title', 'event.date', 'message', 'actionUrl', 'actionLabel'],
      required: ['user.firstName', 'club.name']
    }
  };

  // Load template
  useEffect(() => {
    const loadTemplate = async () => {
      setLoading(true);
      try {
        let templateData = initialTemplate || defaultTemplate;
        
        // Normalize field names
        if (templateData.languages) {
          templateData.languages = templateData.languages.map(lang => ({
            ...lang,
            content: {
              ...lang.content,
              emailHtml: lang.content.emailHtml || lang.content.bodyHtml || '',
              emailText: lang.content.emailText || lang.content.bodyText || '',
              subject: lang.content.subject || '',
              inAppTitle: lang.content.inAppTitle || '',
              inAppMessage: lang.content.inAppMessage || ''
            }
          }));
        }
        
        setTemplate(templateData);
        if (templateData.languages?.length > 0) {
          setSelectedLanguage(templateData.languages[0].code);
        }
      } catch (error) {
        console.error('Error loading template:', error);
        notify('Failed to load template', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [clubId, templateType, initialTemplate]);

  // Get current language content
  const getCurrentContent = () => {
    if (!template?.languages) return null;
    return template.languages.find(lang => lang.code === selectedLanguage);
  };

  // Update content
  const updateContent = (field, value) => {
    if (!template) return;
    const updated = { ...template };
    const langIndex = updated.languages.findIndex(lang => lang.code === selectedLanguage);
    if (langIndex >= 0) {
      updated.languages[langIndex].content[field] = value;
      setTemplate(updated);
      validateTemplate(updated);
    }
  };

  // Insert variable at cursor
  const insertVariable = (varKey) => {
    const field = activeField;
    if (!field || !fieldRefs.current[field]) return;
    
    const input = fieldRefs.current[field];
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = getCurrentContent()?.content[field] || '';
    const varText = `{{${varKey}}}`;
    const newValue = currentValue.substring(0, start) + varText + currentValue.substring(end);
    
    updateContent(field, newValue);
    
    // Restore cursor position after variable
    setTimeout(() => {
      input.focus();
      const newPos = start + varText.length;
      input.setSelectionRange(newPos, newPos);
    }, 0);
    
    notify(`Inserted {{${varKey}}}`, 'success');
  };

  // Copy variable to clipboard
  const copyVariable = (varKey) => {
    navigator.clipboard.writeText(`{{${varKey}}}`);
    notify(`Copied {{${varKey}}} to clipboard`, 'success');
  };

  // Validate template
  const validateTemplate = (templateData) => {
    try {
      const validation = validateTemplateComprehensive(templateData, selectedLanguage);
      setValidationErrors(validation.errors || []);
      setValidationWarnings(validation.warnings || []);
      return validation;
    } catch (error) {
      console.error('Validation error:', error);
      return { isValid: true, errors: [], warnings: [] };
    }
  };

  // Generate preview
  const handlePreview = async () => {
    if (!template) return;
    
    const currentLang = getCurrentContent();
    if (!currentLang) return;
    
    const mockData = {
      user: { firstName: 'John', lastName: 'Doe', fullName: 'John Doe', email: 'john@example.com' },
      club: { name: 'Eagles Soccer Club', sport: 'Soccer' },
      program: { name: 'Youth Training', startDate: 'January 15, 2024' },
      event: { title: 'Practice Match', date: 'January 20, 2024', location: 'Main Field' },
      message: 'This is a sample notification message.',
      actionUrl: '/dashboard',
      actionLabel: 'View Details'
    };
    
    const render = (text) => {
      if (!text) return '';
      return text.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
        const keys = key.trim().split('.');
        let value = mockData;
        for (const k of keys) {
          value = value?.[k];
        }
        return value || match;
      });
    };
    
    setPreviewContent({
      inAppTitle: render(currentLang.content.inAppTitle),
      inAppMessage: render(currentLang.content.inAppMessage),
      subject: render(currentLang.content.subject),
      emailHtml: render(currentLang.content.emailHtml),
      emailText: render(currentLang.content.emailText)
    });
    setShowPreview(true);
  };

  // Send test
  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      notify('Please enter a test email', 'error');
      return;
    }
    
    setSendingTest(true);
    try {
      const currentLang = getCurrentContent();
      const testData = {
        language: selectedLanguage,
        testEmail: testEmail.trim(),
        sampleData: {
          user: { firstName: 'Test', lastName: 'User', fullName: 'Test User', email: testEmail },
          club: { name: 'Test Club' },
          message: 'This is a test notification'
        },
        templateContent: currentLang?.content
      };
      
      const response = await notificationApi.sendTestNotification(clubId, templateType, testData);
      if (response?.success || response?.data?.success) {
        notify(`Test sent to ${testEmail}`, 'success');
        setTestEmail('');
        setShowTestPanel(false);
      } else {
        throw new Error(response?.message || 'Failed to send test');
      }
    } catch (error) {
      notify(error.message || 'Failed to send test', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  // Save template
  const handleSave = async () => {
    if (validationErrors.length > 0) {
      notify('Please fix validation errors first', 'error');
      return;
    }
    
    setSaving(true);
    try {
      if (clubId && templateType) {
        await notificationApi.updateTemplate(clubId, templateType, template);
      }
      notify('Template saved successfully', 'success');
      onSave?.(template);
    } catch (err) {
      notify(err.message || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Reset template
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  
  const handleReset = () => {
    setShowResetConfirm(true);
  };

  const confirmReset = () => {
    setShowResetConfirm(false);
    setTemplate({ ...defaultTemplate });
    setValidationErrors([]);
    notify('Template reset', 'info');
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner}>⏳</div>
        <p>Loading template...</p>
      </div>
    );
  }

  if (!template) {
    return (
      <div className={styles.errorState}>
        <p>Failed to load template</p>
        <button onClick={onCancel}>Go Back</button>
      </div>
    );
  }

  const content = getCurrentContent()?.content || {};

  return (
    <div className={styles.editor}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleArea}>
            <h1 className={styles.title}>Edit Template</h1>
            <span className={styles.templateName}>{template.name}</span>
          </div>
          
          <div className={styles.headerActions}>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className={styles.langSelect}
            >
              {languages.map(l => (
                <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
              ))}
            </select>
            <button onClick={handlePreview} className={styles.btnIcon} title="Preview">
              <FaEye />
            </button>
            <button onClick={() => setShowTestPanel(!showTestPanel)} className={styles.btnIcon} title="Send Test">
              <FaPaperPlane />
            </button>
            <button onClick={handleReset} className={styles.btnIcon} title="Reset to Default">
              <FaUndo />
            </button>
            <button onClick={handleSave} className={styles.btnPrimary} disabled={saving || validationErrors.length > 0}>
              <FaSave /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={onCancel} className={styles.btnClose}>
              <FaTimes />
            </button>
          </div>
        </div>
      </header>

      {/* Test Panel (Collapsible) */}
      {showTestPanel && (
        <div className={styles.testPanel}>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter test email..."
            className={styles.testInput}
          />
          <button onClick={handleSendTest} disabled={sendingTest || !testEmail.trim()} className={styles.btnPrimary}>
            {sendingTest ? 'Sending...' : 'Send Test Email'}
          </button>
        </div>
      )}

      {/* Validation Messages */}
      {validationErrors.length > 0 && (
        <div className={styles.validationError}>
          <FaExclamationTriangle />
          {validationErrors.map((err, i) => <span key={i}>{err}</span>)}
        </div>
      )}

      <div className={styles.mainContent}>
        {/* Left: Editor */}
        <div className={styles.editorPanel}>
          {/* Tabs */}
          <div className={styles.tabs}>
            <button 
              className={`${styles.tab} ${activeTab === 'inApp' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('inApp')}
            >
              <FaMobile /> In-App
            </button>
            <button 
              className={`${styles.tab} ${activeTab === 'email' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('email')}
            >
              <FaEnvelope /> Email
            </button>
          </div>

          {/* In-App Fields */}
          {activeTab === 'inApp' && (
            <div className={styles.tabContent}>
              <div className={styles.field}>
                <label>Title <span className={styles.required}>*</span></label>
                <input
                  ref={el => fieldRefs.current.inAppTitle = el}
                  type="text"
                  value={content.inAppTitle || ''}
                  onChange={(e) => updateContent('inAppTitle', e.target.value)}
                  onFocus={() => setActiveField('inAppTitle')}
                  placeholder="Notification title..."
                />
              </div>
              
              <div className={styles.field}>
                <label>Message <span className={styles.required}>*</span></label>
                <textarea
                  ref={el => fieldRefs.current.inAppMessage = el}
                  value={content.inAppMessage || ''}
                  onChange={(e) => updateContent('inAppMessage', e.target.value)}
                  onFocus={() => setActiveField('inAppMessage')}
                  placeholder="Notification message..."
                  rows={4}
                />
              </div>
            </div>
          )}

          {/* Email Fields */}
          {activeTab === 'email' && (
            <div className={styles.tabContent}>
              <div className={styles.field}>
                <label>Subject Line</label>
                <input
                  ref={el => fieldRefs.current.subject = el}
                  type="text"
                  value={content.subject || ''}
                  onChange={(e) => updateContent('subject', e.target.value)}
                  onFocus={() => setActiveField('subject')}
                  placeholder="Email subject..."
                />
              </div>
              
              <div className={styles.fieldGroup}>
                <div className={styles.fieldLarge}>
                  <label>
                    <FaCode className={styles.labelIcon} />
                    Email Body (HTML)
                  </label>
                  <textarea
                    ref={el => fieldRefs.current.emailHtml = el}
                    value={content.emailHtml || ''}
                    onChange={(e) => updateContent('emailHtml', e.target.value)}
                    onFocus={() => setActiveField('emailHtml')}
                    placeholder="Enter HTML email content..."
                    rows={12}
                    className={styles.codeTextarea}
                  />
                </div>
                
                <div className={styles.fieldSmall}>
                  <label>Plain Text Version</label>
                  <textarea
                    ref={el => fieldRefs.current.emailText = el}
                    value={content.emailText || ''}
                    onChange={(e) => updateContent('emailText', e.target.value)}
                    onFocus={() => setActiveField('emailText')}
                    placeholder="Plain text fallback..."
                    rows={12}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: Variables */}
        <div className={styles.variablesPanel}>
          <h3>Insert Variables</h3>
          <p className={styles.variablesHint}>Click to insert at cursor, or copy</p>
          
          {variableCategories.map(cat => (
            <div key={cat.name} className={styles.varCategory}>
              <h4>{cat.name}</h4>
              <div className={styles.varList}>
                {cat.vars.map(v => (
                  <div key={v.key} className={styles.varItem}>
                    <button 
                      className={styles.varBtn}
                      onClick={() => insertVariable(v.key)}
                      disabled={!activeField}
                      title={`Insert {{${v.key}}}`}
                    >
                      <FaPlus className={styles.varAddIcon} />
                      <span className={styles.varLabel}>{v.label}</span>
                    </button>
                    <button 
                      className={styles.varCopyBtn}
                      onClick={() => copyVariable(v.key)}
                      title="Copy to clipboard"
                    >
                      <FaCopy />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && previewContent && (
        <div className={styles.previewOverlay} onClick={() => setShowPreview(false)}>
          <div className={styles.previewModal} onClick={e => e.stopPropagation()}>
            <div className={styles.previewHeader}>
              <h2>Preview</h2>
              <button onClick={() => setShowPreview(false)} className={styles.closeBtn}>
                <FaTimes />
              </button>
            </div>
            
            <div className={styles.previewTabs}>
              <button 
                className={`${styles.previewTab} ${activeTab === 'inApp' ? styles.previewTabActive : ''}`}
                onClick={() => setActiveTab('inApp')}
              >
                <FaMobile /> In-App
              </button>
              <button 
                className={`${styles.previewTab} ${activeTab === 'email' ? styles.previewTabActive : ''}`}
                onClick={() => setActiveTab('email')}
              >
                <FaEnvelope /> Email
              </button>
            </div>
            
            <div className={styles.previewContent}>
              {activeTab === 'inApp' ? (
                <div className={styles.inAppPreview}>
                  <div className={styles.phoneFrame}>
                    <div className={styles.notification}>
                      <div className={styles.notifIcon}>🔔</div>
                      <div className={styles.notifContent}>
                        <strong>{previewContent.inAppTitle}</strong>
                        <p>{previewContent.inAppMessage}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.emailPreview}>
                  <div className={styles.emailFrame}>
                    <div className={styles.emailSubject}>
                      <strong>Subject:</strong> {previewContent.subject}
                    </div>
                    <div 
                      className={styles.emailBody}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent.emailHtml || '') }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditor;
